import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Reveal from "@/components/Reveal";

/* eslint-disable react/no-unescaped-entities */

// ─────────────────────────────────────────────────────────────
// CGU — Conditions Générales d'Utilisation
// 12 sections. Données société récupérées depuis /public/legal.
// ─────────────────────────────────────────────────────────────

const API = process.env.REACT_APP_BACKEND_URL;

export default function CGU() {
    const [legal, setLegal] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/api/public/legal`)
            .then(({ data }) => setLegal(data.legal))
            .catch(() => {});
    }, []);

    const brand = legal?.brand_name || "XV Capital Partners";
    const company = legal?.company_name || "XV INVESTISSEMENT HOLDING GROUP";

    return (
        <div data-testid="cgu-page" className="bg-xv-ivory">
            {/* HERO */}
            <section className="bg-xv-midnightDeep text-xv-ivory pt-32 md:pt-40 pb-20 md:pb-24">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-8 text-xv-goldSoft">
                            <span className="block h-px w-12 bg-xv-goldSoft" />
                            <span className="overline">
                                Conditions Générales d'Utilisation
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-ivory text-[36px] sm:text-5xl lg:text-[60px] leading-[1.04] tracking-tight max-w-3xl"
                            style={{ fontWeight: 300 }}
                        >
                            Cadre d'usage du site, en toute transparence.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-9 text-xv-ivory/70 text-[15px] md:text-[16px] leading-[1.9] font-light max-w-2xl">
                            Les présentes Conditions Générales d'Utilisation
                            définissent les modalités d'accès et
                            d'utilisation du site internet de {brand}.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-xv-ivory py-20 md:py-28">
                <div className="max-w-[920px] mx-auto px-6 md:px-12 space-y-14">
                    <Block title="1. Objet">
                        <P>
                            Les présentes Conditions Générales d'Utilisation
                            (ci-après « CGU ») définissent les modalités
                            d'accès et d'utilisation du site internet de{" "}
                            {brand}.
                        </P>
                        <P>
                            Le site a pour vocation de présenter les
                            activités du cabinet et de permettre une prise
                            de contact confidentielle avec les équipes.
                        </P>
                    </Block>

                    <Block title="2. Éditeur">
                        <P>
                            <strong className="text-xv-black font-semibold">
                                {brand}
                            </strong>{" "}
                            est une marque exploitée par{" "}
                            <strong className="text-xv-black font-semibold">
                                {company}
                            </strong>
                            , société par actions simplifiée de droit français.
                        </P>
                        <P>
                            Les informations juridiques (SIREN, siège social,
                            directeur de la publication) sont disponibles sur
                            la page{" "}
                            <Link
                                to="/mentions-legales"
                                className="link-underline text-xv-midnight"
                            >
                                Mentions légales
                            </Link>
                            .
                        </P>
                    </Block>

                    <Block title="3. Nature des informations">
                        <P>
                            Les informations diffusées sur le site sont
                            fournies à titre exclusivement informatif. Elles
                            ne constituent en aucun cas :
                        </P>
                        <ul className="space-y-2 mt-3 list-disc pl-6 text-[15px] text-xv-text-secondary leading-relaxed font-light">
                            <li>ni une consultation juridique,</li>
                            <li>ni une consultation fiscale,</li>
                            <li>ni un conseil comptable,</li>
                            <li>ni un conseil en investissement personnalisé,</li>
                            <li>ni une offre au public,</li>
                            <li>
                                ni une garantie d'obtention d'un financement ou
                                de réalisation d'une opération.
                            </li>
                        </ul>
                    </Block>

                    <Block title="4. Relation contractuelle">
                        <P>
                            La consultation du site ne crée aucune relation
                            contractuelle entre l'utilisateur et {brand}.
                        </P>
                        <P>
                            Toute mission fait l'objet d'un échange préalable,
                            d'une analyse, et, le cas échéant, d'une lettre
                            de mission ou d'un contrat spécifique.
                        </P>
                    </Block>

                    <Block title="5. Obligation de moyens">
                        <P>
                            {brand} intervient dans le cadre d'une obligation
                            de moyens. Le cabinet met en œuvre toutes les
                            diligences nécessaires mais ne garantit pas :
                        </P>
                        <ul className="space-y-2 mt-3 list-disc pl-6 text-[15px] text-xv-text-secondary leading-relaxed font-light">
                            <li>l'obtention d'un financement,</li>
                            <li>la conclusion d'une opération,</li>
                            <li>la réalisation d'une acquisition,</li>
                            <li>la réussite d'une structuration capitalistique,</li>
                            <li>ni la signature d'un partenariat.</li>
                        </ul>
                    </Block>

                    <Block title="6. Utilisation du site">
                        <P>
                            L'utilisateur s'engage à utiliser le site
                            conformément à la réglementation applicable et
                            aux présentes CGU. Il s'interdit notamment :
                        </P>
                        <ul className="space-y-2 mt-3 list-disc pl-6 text-[15px] text-xv-text-secondary leading-relaxed font-light">
                            <li>toute utilisation frauduleuse,</li>
                            <li>toute tentative d'intrusion,</li>
                            <li>toute extraction automatisée des données,</li>
                            <li>toute reproduction non autorisée des contenus.</li>
                        </ul>
                    </Block>

                    <Block title="7. Propriété intellectuelle">
                        <P>
                            L'ensemble des éléments du site est protégé par
                            le Code de la propriété intellectuelle : logo,
                            identité visuelle, textes, méthodologie, concepts,
                            documents, illustrations et architecture du site.
                        </P>
                        <P>
                            Toute reproduction totale ou partielle est
                            interdite sans autorisation écrite préalable.
                        </P>
                    </Block>

                    <Block title="8. Disponibilité">
                        <P>
                            {brand} s'efforce d'assurer la disponibilité du
                            site mais ne garantit pas une disponibilité
                            permanente. Le site peut être interrompu pour
                            maintenance ou mise à jour, sans préavis.
                        </P>
                    </Block>

                    <Block title="9. Responsabilité">
                        <P>
                            {brand} ne pourra être tenu responsable
                            notamment :
                        </P>
                        <ul className="space-y-2 mt-3 list-disc pl-6 text-[15px] text-xv-text-secondary leading-relaxed font-light">
                            <li>
                                des décisions prises par les utilisateurs sur la
                                base des informations publiées,
                            </li>
                            <li>des contenus des sites tiers,</li>
                            <li>des interruptions techniques,</li>
                            <li>
                                des conséquences liées à une mauvaise
                                utilisation du site.
                            </li>
                        </ul>
                    </Block>

                    <Block title="10. Données personnelles">
                        <P>
                            Les modalités de traitement de vos données
                            personnelles sont décrites dans la{" "}
                            <Link
                                to="/confidentialite"
                                className="link-underline text-xv-midnight"
                            >
                                Politique de confidentialité
                            </Link>
                            , qui détaille également la politique de
                            cookies.
                        </P>
                    </Block>

                    <Block title="11. Modification des CGU">
                        <P>
                            {brand} peut modifier les présentes Conditions
                            Générales d'Utilisation à tout moment. La version
                            applicable est celle publiée sur le site à la
                            date de consultation.
                        </P>
                    </Block>

                    <Block title="12. Droit applicable">
                        <P>
                            Les présentes CGU sont régies par le droit
                            français.
                        </P>
                        <P>
                            Tout litige relatif à l'utilisation du site
                            relève de la compétence des juridictions
                            françaises compétentes.
                        </P>
                    </Block>
                </div>
            </section>
        </div>
    );
}

function Block({ title, children }) {
    return (
        <Reveal>
            <section>
                <h2
                    className="font-serif text-xv-black text-[22px] md:text-[26px] leading-tight mb-6 pb-3 border-b border-xv-line"
                    style={{ fontWeight: 400 }}
                >
                    {title}
                </h2>
                <div className="space-y-4">{children}</div>
            </section>
        </Reveal>
    );
}

function P({ children }) {
    return (
        <p className="text-[15px] md:text-[16px] text-xv-text-secondary leading-[1.9] font-light">
            {children}
        </p>
    );
}
